import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Shield, BookOpen, Database, History, ExternalLink, Search, Loader2 } from 'lucide-react';
import { transparencyAPI } from '@/lib/api';

export default function Transparency() {
  const { t } = useTranslation();
  const [methodology, setMethodology] = useState(null);
  const [factors, setFactors] = useState([]);
  const [sources, setSources] = useState({});
  const [changelog, setChangelog] = useState([]);
  const [search, setSearch] = useState('');
  const [energyType, setEnergyType] = useState('all');
  const [recentEdits, setRecentEdits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [m, f, c, r] = await Promise.all([
          transparencyAPI.methodology(),
          transparencyAPI.factors(),
          transparencyAPI.changelog(),
          transparencyAPI.recentEdits(5).catch(() => ({ data: { edits: [] } })),
        ]);
        setMethodology(m.data.methodology);
        setSources(m.data.sources || {});
        setFactors(f.data.factors || []);
        setChangelog(c.data.changelog || []);
        setRecentEdits(r.data.edits || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = factors.filter((f) => {
    const matchesType = energyType === 'all' || f.energy_type === energyType;
    const q = search.trim().toLowerCase();
    const matchesSearch = !q
      || (f.country || '').toLowerCase().includes(q)
      || (f.source || '').toLowerCase().includes(q)
      || (f.material || '').toLowerCase().includes(q);
    return matchesType && matchesSearch;
  });

  const energyTypes = ['all', ...Array.from(new Set(factors.map((f) => f.energy_type)))];

  return (
    <div className="min-h-screen bg-background" data-testid="transparency-page">
      {/* Public header */}
      <header className="border-b border-border bg-card/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <Shield className="w-6 h-6 text-primary" />
          <div>
            <h1 className="font-bold text-lg">Veridex One — Transparency</h1>
            <p className="text-xs text-muted-foreground">Methodology, emission factors, and audit trail</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <>
            {/* Methodology */}
            <Card className="p-6" data-testid="methodology-card">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="w-5 h-5 text-primary" />
                <h2 className="text-xl font-bold">Methodology v{methodology?.version}</h2>
              </div>
              <p className="text-sm text-muted-foreground mb-4">{methodology?.approach}</p>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <h3 className="font-semibold mb-2">Scope</h3>
                  <ul className="space-y-1 text-muted-foreground">
                    {(methodology?.scope || []).map((s, i) => <li key={i}>• {s}</li>)}
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Formula</h3>
                  <pre className="text-xs bg-muted/50 p-2 rounded border border-border whitespace-pre-wrap font-mono">
{methodology?.formula}
                  </pre>
                  <p className="mt-2 text-xs text-muted-foreground">Units: <b>{methodology?.units}</b></p>
                  <p className="text-xs text-muted-foreground">GWP: {methodology?.gwp}</p>
                </div>
              </div>
            </Card>

            {/* Sources */}
            <Card className="p-6" data-testid="sources-card">
              <h2 className="font-semibold mb-3">Data Sources</h2>
              <div className="grid md:grid-cols-2 gap-3 text-sm">
                {Object.entries(sources).map(([k, v]) => (
                  <div key={k} className="flex items-start gap-3 p-3 rounded-md bg-muted/30 border border-border">
                    <Database className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium">{k.replace(/_/g, ' ')}</p>
                      <p className="text-xs text-muted-foreground">{v.source}</p>
                      {v.url && (
                        <a href={v.url} target="_blank" rel="noreferrer"
                           className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1">
                          View source <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Factors table */}
            <Card className="p-0 overflow-hidden" data-testid="factors-table-card">
              <div className="p-5 border-b border-border">
                <h2 className="font-semibold mb-3">Emission Factors Catalog ({factors.length} entries)</h2>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 rtl:left-auto rtl:right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input value={search} onChange={(e) => setSearch(e.target.value)}
                           placeholder="Search country / source / material"
                           className="pl-9 rtl:pl-3 rtl:pr-9" data-testid="factors-search" />
                  </div>
                  <Select value={energyType} onValueChange={setEnergyType}>
                    <SelectTrigger className="w-full sm:w-56" data-testid="factors-type-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {energyTypes.map((t) => (
                        <SelectItem key={t} value={t}>{t === 'all' ? 'All types' : t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground sticky top-0">
                    <tr>
                      <th className="px-4 py-3 font-medium">Energy / Material</th>
                      <th className="px-4 py-3 font-medium">Country</th>
                      <th className="px-4 py-3 font-medium text-right">Factor (kg CO₂/unit)</th>
                      <th className="px-4 py-3 font-medium">Source</th>
                      <th className="px-4 py-3 font-medium">Updated</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filtered.map((f, i) => (
                      <tr key={i} data-testid={`factor-row-${i}`} className="hover:bg-muted/30">
                        <td className="px-4 py-3 font-medium">
                          {f.material ? f.material : f.energy_type}
                        </td>
                        <td className="px-4 py-3"><Badge variant="outline">{f.country}</Badge></td>
                        <td className="px-4 py-3 text-right font-mono">{f.factor_kg_co2_per_unit?.toFixed(3)}</td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">{f.source}</td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">
                          {f.last_updated ? new Date(f.last_updated).toLocaleDateString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* PCF formula explainer — easy-to-read decomposition */}
            <Card className="p-6" data-testid="pcf-formula-card">
              <h2 className="font-semibold mb-3 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-primary" /> How we calculate PCF
              </h2>
              <pre className="text-sm bg-muted/50 p-4 rounded border border-border font-mono overflow-x-auto">
{`PCF = (Energy + Process + Feedstock) / Production`}
              </pre>
              <div className="grid md:grid-cols-4 gap-3 mt-4 text-sm">
                <div className="p-3 rounded-md bg-muted/30 border border-border">
                  <p className="font-semibold text-primary">Energy</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Electricity + fuels consumed during manufacturing, multiplied by the country-specific grid factor.
                  </p>
                </div>
                <div className="p-3 rounded-md bg-muted/30 border border-border">
                  <p className="font-semibold text-primary">Process</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Direct process emissions from chemistry (e.g. calcination, blast-furnace reduction) per IPCC 2024.
                  </p>
                </div>
                <div className="p-3 rounded-md bg-muted/30 border border-border">
                  <p className="font-semibold text-primary">Feedstock</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Upstream embedded emissions of raw materials (cradle-to-gate) from supplier data or Open CEDA factors.
                  </p>
                </div>
                <div className="p-3 rounded-md bg-muted/30 border border-border">
                  <p className="font-semibold text-primary">Production</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Mass of saleable output in the reference period (tons). Divisor that normalizes emissions to product-level intensity.
                  </p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                Output units: <b>tCO2e per ton of product</b> · GWP AR6 (IPCC 2023) · Boundary: cradle-to-gate.
              </p>
            </Card>

            {/* Recent manual edits from change_logs */}
            <Card className="p-6" data-testid="recent-edits-card">
              <h2 className="font-semibold mb-3 flex items-center gap-2">
                <History className="w-5 h-5 text-primary" /> Recent manual edits (last 5)
              </h2>
              {recentEdits.length === 0 ? (
                <p className="text-sm text-muted-foreground">No manual edits recorded yet.</p>
              ) : (
                <div className="cp-table-wrapper">
                  <table className="w-full text-sm min-w-[640px]">
                    <thead className="bg-muted/40">
                      <tr className="text-left">
                        <th className="px-3 py-2 font-medium">When</th>
                        <th className="px-3 py-2 font-medium">User</th>
                        <th className="px-3 py-2 font-medium">Entity</th>
                        <th className="px-3 py-2 font-medium">Field</th>
                        <th className="px-3 py-2 font-medium">Old &rarr; New</th>
                        <th className="px-3 py-2 font-medium">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentEdits.map((e, i) => (
                        <tr key={i} className="border-t border-border" data-testid={`recent-edit-row-${i}`}>
                          <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                            {e.timestamp ? new Date(e.timestamp).toLocaleString() : '—'}
                          </td>
                          <td className="px-3 py-2 text-xs">{e.user_email || e.user || '—'}</td>
                          <td className="px-3 py-2 text-xs">{e.entity_type || e.entity || '—'}</td>
                          <td className="px-3 py-2 text-xs font-mono">{e.field || '—'}</td>
                          <td className="px-3 py-2 text-xs">
                            <span className="text-destructive/70">{String(e.old_value ?? '—').slice(0, 20)}</span>
                            <span className="mx-1 text-muted-foreground">&rarr;</span>
                            <span className="text-primary">{String(e.new_value ?? '—').slice(0, 20)}</span>
                          </td>
                          <td className="px-3 py-2 text-xs text-muted-foreground">{e.reason || e.note || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {/* Changelog */}
            <Card className="p-6" data-testid="changelog-card">
              <div className="flex items-center gap-2 mb-4">
                <History className="w-5 h-5 text-primary" />
                <h2 className="font-semibold">Methodology Changelog</h2>
              </div>
              <div className="space-y-3">
                {changelog.map((c, i) => (
                  <div key={i} className="flex gap-3 border-l-2 border-primary/30 pl-3" data-testid={`changelog-entry-${i}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 text-sm">
                        <Badge variant="secondary" className="font-mono text-xs">v{c.version}</Badge>
                        <span className="text-xs text-muted-foreground">{c.date}</span>
                      </div>
                      <p className="text-sm mt-1">{c.change}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">— {c.by}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
